import { useEffect, useState, useMemo, useCallback } from 'react';
import { ArrowLeft, CheckCircle2, RefreshCw } from 'lucide-react';
import { getEventHistory, acknowledgeAlert } from '../../services/eventWatcherApi';

const CENTRAL_TZ = 'America/Chicago';

const fmtCentral = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: CENTRAL_TZ,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
};

const SEVERITY_STYLES = {
  Critical: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  High:     'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  Medium:   'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  Low:      'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  Info:     'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

export default function EventHistory({ monitoredEventId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getEventHistory(monitoredEventId);
      setData(resp);
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load event history');
    } finally {
      setLoading(false);
    }
  }, [monitoredEventId]);

  useEffect(() => {
    load();
  }, [load]);

  const ack = async (id) => {
    try {
      await acknowledgeAlert(id);
      await load();
    } catch (e) {
      alert(`Failed to acknowledge: ${e.message}`);
    }
  };

  // Chronological snapshots for the chart table (oldest -> newest).
  const chronSnapshots = useMemo(() => {
    if (!data?.snapshots) return [];
    return [...data.snapshots].sort(
      (a, b) => new Date(a.capturedAt) - new Date(b.capturedAt)
    );
  }, [data]);

  if (loading && !data) {
    return (
      <div className="p-6 text-center text-slate-500 dark:text-slate-400">
        Loading history…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <BackBar onBack={onBack} />
        <div className="p-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const e = data.event;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <BackBar onBack={onBack} onReload={load} loading={loading} />

      {/* Event header */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 mb-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {e.tenant}
        </p>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">
          {e.title || `Event #${e.tenantEventId}`}
        </h1>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
          <KV label="Status" value={e.status} />
          <KV label="Scheduled" value={fmtCentral(e.eventDateTime)} />
          <KV label="Watching since" value={fmtCentral(e.startedMonitoringAt)} />
          <KV label="Stopped at" value={fmtCentral(e.stoppedMonitoringAt)} />
          <KV label="Peak connections" value={e.peakConnections ?? '—'} />
          <KV label="Snapshots taken" value={e.totalSnapshots} />
          <KV label="Alerts fired" value={e.totalAlerts} />
          <KV label="Discovered at" value={fmtCentral(e.discoveredAt)} />
        </div>
      </div>

      {/* Alerts */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 mb-4">
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700">
          <h2 className="font-semibold text-slate-900 dark:text-slate-100">
            Alerts ({data.alerts.length})
          </h2>
        </div>
        {data.alerts.length === 0 ? (
          <p className="p-5 text-sm text-slate-500 dark:text-slate-400">No alerts fired for this event.</p>
        ) : (
          <div className="divide-y divide-slate-200 dark:divide-slate-700">
            {data.alerts.map((a) => (
              <div key={a.id} className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.Info}`}>
                        {a.severity}
                      </span>
                      <span className="font-medium text-slate-900 dark:text-slate-100">{a.ruleName}</span>
                      {a.acknowledged && (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400">
                          <CheckCircle2 size={12} /> ack'd by {a.acknowledgedBy}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 dark:text-slate-200 mt-1">{a.message}</p>
                    {a.suggestion && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 italic">
                        Suggestion: {a.suggestion}
                      </p>
                    )}
                    {a.suggestedCommand && (
                      <pre className="text-xs bg-slate-900 text-slate-100 rounded p-2 mt-2 overflow-x-auto">
                        {a.suggestedCommand}
                      </pre>
                    )}
                    <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-2">
                      Fired {fmtCentral(a.firedAt)}
                    </p>
                  </div>
                  {!a.acknowledged && (
                    <button
                      onClick={() => ack(a.id)}
                      className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 shrink-0"
                    >
                      Acknowledge
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Snapshots table (compact) */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h2 className="font-semibold text-slate-900 dark:text-slate-100">
            Snapshots ({data.snapshots.length})
          </h2>
          <span className="text-xs text-slate-500 dark:text-slate-400">oldest → newest</span>
        </div>
        {chronSnapshots.length === 0 ? (
          <p className="p-5 text-sm text-slate-500 dark:text-slate-400">
            No snapshots collected yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 dark:bg-slate-800/70">
                <tr>
                  <Th>Time (CT)</Th>
                  <Th>Conns</Th>
                  <Th>Closes</Th>
                  <Th>Msgs</Th>
                  <Th>Load %</Th>
                  <Th>Reqs</Th>
                  <Th>5xx</Th>
                  <Th>Avg ms</Th>
                  <Th>HubExc</Th>
                  <Th>OtherExc</Th>
                </tr>
              </thead>
              <tbody>
                {chronSnapshots.map((s) => {
                  const danger = (s.hubConnExceptions || 0) > 0;
                  return (
                    <tr
                      key={s.id}
                      className={`border-t border-slate-200 dark:border-slate-700 ${
                        danger ? 'bg-red-50 dark:bg-red-900/20' : ''
                      }`}
                    >
                      <Td>{fmtCentral(s.capturedAt)}</Td>
                      <Td>{s.connectionCount ?? '—'}</Td>
                      <Td>{s.connectionCloseCount ?? '—'}</Td>
                      <Td>{s.messageCount ?? '—'}</Td>
                      <Td>{s.serverLoadPct != null ? Number(s.serverLoadPct).toFixed(1) : '—'}</Td>
                      <Td>{s.requests ?? '—'}</Td>
                      <Td>{s.http5xxCount ?? '—'}</Td>
                      <Td>{s.avgResponseMs != null ? Number(s.avgResponseMs).toFixed(0) : '—'}</Td>
                      <Td className={danger ? 'font-bold text-red-700 dark:text-red-300' : ''}>
                        {s.hubConnExceptions}
                      </Td>
                      <Td>{s.otherExceptions}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function BackBar({ onBack, onReload, loading }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100"
      >
        <ArrowLeft size={16} /> Back to active watches
      </button>
      {onReload && (
        <button
          onClick={onReload}
          disabled={loading}
          className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Reload
        </button>
      )}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</p>
      <p className="text-slate-900 dark:text-slate-100">{value ?? '—'}</p>
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
      {children}
    </th>
  );
}

function Td({ children, className = '' }) {
  return (
    <td className={`px-3 py-1.5 text-slate-700 dark:text-slate-200 whitespace-nowrap ${className}`}>
      {children}
    </td>
  );
}
